import { useEffect, useMemo, useRef, useState } from 'react';
import { announcementService, Announcement } from '../../services/announcement.service';
import styles from './AnnouncementBanner.module.css';

const DISMISS_KEY = 'announcement_dismissed';

/**
 * How much of the banner is on screen, for the fixed top bars
 * (`.top-nav`) to sit below. The banner is in the page flow, but those bars
 * are `position: fixed; top: 0`, so without this the banner lay over the
 * menu, logo, language switch and the signed-in avatar until it was closed
 * (screen tests, 24 Sep 2026). Tracked on scroll, so a bar slides back to the
 * top as the banner scrolls away.
 */
const OFFSET_VAR = '--announcement-offset';

// Small stable hash so dismissal is tied to the exact message — a NEW
// announcement re-shows even if a previous one was dismissed.
const hashMessage = (message: string): string => {
  let hash = 0;
  for (let i = 0; i < message.length; i++) {
    hash = (hash << 5) - hash + message.charCodeAt(i);
    hash |= 0;
  }
  return String(hash);
};

/**
 * Global announcement banner shown across all storefronts. Content is managed
 * in Strapi (single type). Renders nothing when there is no active announcement.
 */
export const AnnouncementBanner = () => {
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);
  const visible = !!announcement && !dismissed;

  useEffect(() => {
    const el = bannerRef.current;
    const root = document.documentElement;
    if (!visible || !el) {
      root.style.removeProperty(OFFSET_VAR);
      return;
    }
    const update = () => {
      const bottom = Math.max(0, Math.round(el.getBoundingClientRect().bottom));
      root.style.setProperty(OFFSET_VAR, `${bottom}px`);
    };
    update();
    // Capture, so a page that scrolls an inner container is followed too.
    window.addEventListener('scroll', update, { passive: true, capture: true });
    window.addEventListener('resize', update);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    observer?.observe(el);
    return () => {
      window.removeEventListener('scroll', update, { capture: true });
      window.removeEventListener('resize', update);
      observer?.disconnect();
      root.style.removeProperty(OFFSET_VAR);
    };
  }, [visible]);

  useEffect(() => {
    let mounted = true;
    announcementService
      .getAnnouncement()
      .then((data) => {
        if (!mounted) return;
        setAnnouncement(data);
        if (data) {
          const stored = localStorage.getItem(DISMISS_KEY);
          setDismissed(stored === hashMessage(data.message));
        }
      })
      .catch(() => {
        /* service already logs + falls back */
      });
    return () => {
      mounted = false;
    };
  }, []);

  const variantClass = useMemo(() => {
    if (!announcement) return styles.info;
    return styles[announcement.variant] || styles.info;
  }, [announcement]);

  if (!visible || !announcement) return null;

  const handleClose = () => {
    try {
      localStorage.setItem(DISMISS_KEY, hashMessage(announcement.message));
    } catch {
      /* ignore storage errors */
    }
    setDismissed(true);
  };

  return (
    <div
      ref={bannerRef}
      className={`${styles.banner} ${variantClass}`}
      role="status"
      aria-live="polite"
    >
      <p className={styles.message}>
        {announcement.message}
        {announcement.linkUrl && (
          <>
            {' '}
            <a
              className={styles.link}
              href={announcement.linkUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {announcement.linkLabel || 'Learn more'}
            </a>
          </>
        )}
      </p>
      <button
        type="button"
        className={styles.close}
        onClick={handleClose}
        aria-label="Dismiss announcement"
      >
        ×
      </button>
    </div>
  );
};

export default AnnouncementBanner;
